import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { FlexBox, Modal, ModalBody, ModalFoot, ModalHead, } from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import { useMemo } from 'react';
import { useEffectOnce } from 'react-use';
import { KeyboardKeyCombinations } from 'components/keyboard';
import { useKeyCombinationRecorder } from 'utils/keyboard';
import { createAppModal } from 'utils/modal';
import { useNavigatePrompt } from 'utils/url/useNavigatePrompt';
import styles from './keyCombinationRecorderModal.module.scss';
/** Represents the modal for recording key combinations. */
export const KeyCombinationRecorderModal = createAppModal({ type: 'app:keyCombinationRecorder' }, ({ isOpen, modal, meta, hide }) => {
    var _a, _b, _c, _d, _e, _f, _g;
    const { keyCombination, startRecording, stopRecording } = useKeyCombinationRecorder();
    const title = (_a = modal.arg.title) !== null && _a !== void 0 ? _a : 'Keyboard key combination recorder';
    const description = modal.arg.description;
    const handleCancel = () => {
        meta === null || meta === void 0 ? void 0 : meta.resolve({
            isConfirmed: false,
            isCancelled: true,
        });
        hide === null || hide === void 0 ? void 0 : hide();
    };
    const handleConfirm = () => {
        meta === null || meta === void 0 ? void 0 : meta.resolve({
            isConfirmed: true,
            isCancelled: false,
            keyCombination,
        });
        hide === null || hide === void 0 ? void 0 : hide();
    };
    // Handles starting and stopping the recording when the modal state changes.
    useEffectOnce(() => {
        startRecording();
        return () => {
            stopRecording();
        };
    });
    useNavigatePrompt();
    const conflict = useMemo(() => {
        if (keyCombination && modal.arg.conflictFn) {
            return modal.arg.conflictFn(keyCombination);
        }
        return false;
    }, [keyCombination, modal.arg]);
    const hasKeyCombination = !!keyCombination;
    const hasKey = !!((_b = keyCombination === null || keyCombination === void 0 ? void 0 : keyCombination.binding) === null || _b === void 0 ? void 0 : _b[1]);
    const isKeyCombinationInConflict = !!conflict;
    const isValid = hasKey && !isKeyCombinationInConflict;
    const isConfirmDisabled = !isValid;
    return (_jsxs(Modal, Object.assign({ isOpen: isOpen, onClose: handleCancel, className: styles.root }, { children: [_jsx(ModalHead, { title: title, onClose: () => handleCancel() }), _jsx(ModalBody, { children: _jsxs(FlexBox, Object.assign({ flexDirection: "column", gap: "300" }, { children: [description, _jsx(FlexBox, Object.assign({ className: classnames(styles.preview, {
                                [styles.isValid]: hasKeyCombination && isValid,
                                [styles.isInvalid]: hasKeyCombination && !isValid,
                            }), alignItems: "center", justifyContent: "center" }, { children: keyCombination ? (_jsx(KeyboardKeyCombinations, { keyCombinations: [keyCombination], variant: "rich" })) : ('Press a combination of keys to record') })), typeof conflict !== 'boolean' && conflict.message && (_jsx(FlexBox, { children: conflict.message }))] })) }), _jsx(ModalFoot, { cancelName: (_d = (_c = modal.arg.cancel) === null || _c === void 0 ? void 0 : _c.label) !== null && _d !== void 0 ? _d : 'Cancel', cancelOnClick: handleCancel, cancelProps: {
                    variant: 'secondary',
                }, confirmName: (_f = (_e = modal.arg.confirm) === null || _e === void 0 ? void 0 : _e.label) !== null && _f !== void 0 ? _f : 'Confirm', confirmOnClick: handleConfirm, confirmProps: {
                    variant: (_g = modal.arg.confirm) === null || _g === void 0 ? void 0 : _g.variant,
                    disabled: isConfirmDisabled,
                } })] })));
});
